import React, { useState, useEffect } from 'react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'
import ClientesPorRegiaoCard from '../../../components/vision/ClientesPorRegiaoCard'
import type { Cliente, DashboardMetrics } from '../../../lib/supabase'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'

const DashboardPage: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | '7d' | '30d' | '90d' | 'thisMonth' | 'custom'>('30d')
  const [recentClients, setRecentClients] = useState<Cliente[]>([])
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false)
  const [showDropdown, setShowDropdown] = useState<boolean>(false)
  const [showCustomDates, setShowCustomDates] = useState<boolean>(false)

  const fetchDashboardData = async () => {
    try {
      console.log('🔍 Carregando dados do dashboard via API...')
      setLoading(true)

      // Construir URL com parâmetros baseados no tipo de filtro
      let apiUrl = `/api/gestao/dashboard?dateRange=${dateRange}`
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        apiUrl += `&startDate=${customStartDate}&endDate=${customEndDate}`
      }
      
      const response = await fetch(apiUrl)
      
      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }
      
      const result = await response.json()
      console.log('✅ Dashboard carregado via API:', result.data)
      
      if (result.error) {
        throw new Error(result.error)
      }

      setMetrics(result.data.metrics)
      setRecentClients(result.data.recentClients || [])

      console.log('📊 Métricas carregadas:', result.data.metrics)
      console.log('👥 Clientes recentes:', result.data.recentClients?.length || 0)
      
    } catch (error) {
      console.error('❌ Erro ao carregar dashboard:', error)
      // Em caso de erro, usar dados vazios mas não quebrar a interface
      const emptyMetrics: DashboardMetrics = {
        totalClientes: 0,
        clientesAtivos: 0,
        clientesInativos: 0,
        novosClientesHoje: 0,
        novosClientesSemana: 0,
        novosClientesMes: 0,
        crescimentoPercentual: 0,
        comparacaoPeriodoAnterior: {
          periodo: dateRange === '7d' ? 'últimos 7 dias' : 'últimos 30 dias',
          variacao: 0,
          tipo: 'aumento'
        }
      }
      setMetrics(emptyMetrics)
      setRecentClients([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [dateRange, customStartDate, customEndDate])

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      console.log('🔄 Auto-refresh ativado - recarregando dados...')
      fetchDashboardData()
    }, 30000) // 30 segundos

    return () => clearInterval(interval)
  }, [autoRefresh, dateRange, customStartDate, customEndDate])

  // Click outside handler para fechar dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (showDropdown && !target.closest('.dropdown-container')) {
        setShowDropdown(false)
        setShowCustomDates(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  // Função para aplicar período personalizado
  const handleApplyCustomPeriod = () => {
    if (!customStartDate || !customEndDate) {
      alert('Por favor, selecione ambas as datas')
      return
    }
    
    if (new Date(customStartDate) > new Date(customEndDate)) {
      alert('A data de início deve ser anterior à data de término')
      return
    }

    setDateRange('custom')
    setShowCustomDates(false)
    setShowDropdown(false)
  }

  // Função para selecionar período predefinido
  const handleSelectPeriod = (period: 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'thisMonth' | 'custom') => {
    if (period === 'custom') {
      setShowCustomDates(true)
    } else {
      setDateRange(period)
      setShowCustomDates(false)
      setShowDropdown(false)
    }
  }

  // Função para obter label do período atual
  const getCurrentPeriodLabel = () => {
    switch (dateRange) {
      case 'today': return 'Hoje'
      case 'yesterday': return 'Ontem'
      case '7d': return 'Últimos 7 dias'
      case '30d': return 'Últimos 30 dias'
      case '90d': return 'Últimos 90 dias'
      case 'thisMonth': return 'Este Mês'
      case 'custom': return customStartDate && customEndDate ? 
        `${customStartDate} até ${customEndDate}` : 'Período Personalizado'
      default: return 'Últimos 30 dias'
    }
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Carregando dashboard...</p>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold gradient-text-orange mb-2">Dashboard</h1>
              <p className="text-gray-600">Visão geral do sistema de gestão de clientes</p>
            </div>

            {/* Filtros de Data */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-4 items-center">
                {/* Dropdown de Filtros */}
                <div className="relative dropdown-container">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-orange-50 transition-all duration-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {getCurrentPeriodLabel()}
                    <svg className={`w-4 h-4 transform transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <div className="p-2">
                        {/* Períodos Predefinidos */}
                        <button
                          onClick={() => handleSelectPeriod('today')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === 'today' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Hoje
                        </button>
                        <button
                          onClick={() => handleSelectPeriod('yesterday')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === 'yesterday' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Ontem
                        </button>
                        <button
                          onClick={() => handleSelectPeriod('7d')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === '7d' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Últimos 7 dias
                        </button>
                        <button
                          onClick={() => handleSelectPeriod('30d')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === '30d' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Últimos 30 dias
                        </button>
                        <button
                          onClick={() => handleSelectPeriod('90d')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === '90d' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Últimos 90 dias
                        </button>
                        <button
                          onClick={() => handleSelectPeriod('thisMonth')}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                            dateRange === 'thisMonth' ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Este Mês
                        </button>
                        
                        <hr className="my-2" />
                        
                        {/* Período Personalizado */}
                        <button
                          onClick={() => handleSelectPeriod('custom')}
                          className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors text-gray-700 font-medium"
                        >
                          Período Personalizado
                        </button>
                        
                        {/* Campos de Data Personalizada */}
                        {showCustomDates && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-md">
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Data de início</label>
                                <input
                                  type="date"
                                  value={customStartDate}
                                  onChange={(e) => setCustomStartDate(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Data de término</label>
                                <input
                                  type="date"
                                  value={customEndDate}
                                  onChange={(e) => setCustomEndDate(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                />
                              </div>
                              <button
                                onClick={handleApplyCustomPeriod}
                                className="w-full px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-md text-sm font-medium hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                              >
                                Aplicar Período
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Auto-refresh Toggle */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      autoRefresh 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-green-50'
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                    Auto-refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Cards de Métricas */}
            {metrics && (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total de Clientes */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-orange-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total de Clientes</p>
                      <p className="text-3xl font-bold gradient-text-orange">{metrics.totalClientes}</p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Clientes Ativos */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-green-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Clientes Ativos</p>
                      <p className="text-3xl font-bold text-green-600">{metrics.clientesAtivos}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Novos Clientes (Período) */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        Novos ({dateRange === '7d' ? '7 dias' : '30 dias'})
                      </p>
                      <p className="text-3xl font-bold text-blue-600">
                        {dateRange === '7d' ? metrics.novosClientesSemana : metrics.novosClientesMes}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Crescimento */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Crescimento</p>
                      <div className="flex items-center">
                        <p className={`text-3xl font-bold ${
                          metrics.comparacaoPeriodoAnterior.tipo === 'aumento' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {metrics.comparacaoPeriodoAnterior.tipo === 'aumento' ? '+' : '-'}
                          {metrics.comparacaoPeriodoAnterior.variacao.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      metrics.comparacaoPeriodoAnterior.tipo === 'aumento' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      <svg className={`w-6 h-6 ${
                        metrics.comparacaoPeriodoAnterior.tipo === 'aumento' ? 'text-green-600' : 'text-red-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                          metrics.comparacaoPeriodoAnterior.tipo === 'aumento' 
                            ? "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" 
                            : "M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
                        } />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card de Comparação Mensal */}
              {metrics?.comparacaoMensal && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-2xl shadow-lg border border-indigo-100">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">📅 Comparação Mensal</h3>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          metrics.comparacaoMensal.tipo === 'aumento' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {metrics.comparacaoMensal.tipo === 'aumento' ? '↗️' : '↘️'} 
                          {metrics.comparacaoMensal.tipo === 'aumento' ? '+' : '-'}
                          {metrics.comparacaoMensal.variacaoPercentual.toFixed(1)}%
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Mês Atual */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-600 mb-1">Mês Atual</p>
                          <p className="text-xs text-gray-500 mb-2 capitalize">{metrics.comparacaoMensal.mesAtual.mes}</p>
                          <p className="text-3xl font-bold text-blue-600">
                            {metrics.comparacaoMensal.mesAtual.quantidade}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">novos clientes</p>
                        </div>
                        
                        {/* Mês Anterior */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-600 mb-1">Mês Anterior</p>
                          <p className="text-xs text-gray-500 mb-2 capitalize">{metrics.comparacaoMensal.mesAnterior.mes}</p>
                          <p className="text-3xl font-bold text-gray-600">
                            {metrics.comparacaoMensal.mesAnterior.quantidade}
                          </p>
                          <p className="text-xs text-gray-600 mt-1">novos clientes</p>
                        </div>
                      </div>
                      
                      {/* Diferença */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Diferença:</span>
                          <span className={`text-lg font-bold ${
                            metrics.comparacaoMensal.diferenca >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {metrics.comparacaoMensal.diferenca >= 0 ? '+' : ''}
                            {metrics.comparacaoMensal.diferenca} clientes
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Seção de Análise Mensal com Gráfico */}
              {metrics?.comparacaoMensal?.historicoMensal && (
                <div className="bg-white rounded-2xl shadow-lg border border-orange-100 mb-8">
                  <div className="p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold gradient-text-orange">📈 Análise Mensal de Novos Clientes</h2>
                    <p className="text-gray-600 text-sm">Histórico dos últimos 6 meses</p>
                  </div>
                  
                  <div className="p-6">
                    {/* Gráfico de Barras */}
                    <div className="mb-8">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={metrics.comparacaoMensal.historicoMensal}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="mes" 
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={{ stroke: '#e5e7eb' }}
                          />
                          <YAxis 
                            tick={{ fill: '#6b7280', fontSize: 12 }}
                            tickLine={{ stroke: '#e5e7eb' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value: number) => [`${value} clientes`, 'Novos Clientes']}
                            labelFormatter={(label) => `Mês: ${label}`}
                          />
                          <Bar dataKey="quantidade" radius={[8, 8, 0, 0]}>
                            {metrics.comparacaoMensal.historicoMensal.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={
                                  index === metrics.comparacaoMensal.historicoMensal.length - 1 
                                    ? '#3b82f6'
                                    : entry.tipo === 'aumento' 
                                      ? '#10b981'
                                      : entry.tipo === 'reducao'
                                        ? '#ef4444'
                                        : '#6b7280'
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Tabela de Histórico */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Mês</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Novos Clientes</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">vs Anterior</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Variação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metrics.comparacaoMensal.historicoMensal.map((month, index) => (
                            <tr 
                              key={index} 
                              className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                                index === metrics.comparacaoMensal.historicoMensal.length - 1 
                                  ? 'bg-blue-50 font-semibold' 
                                  : ''
                              }`}
                            >
                              <td className="py-3 px-4 text-sm text-gray-900 capitalize">
                                {month.mesCompleto}
                                {index === metrics.comparacaoMensal.historicoMensal.length - 1 && (
                                  <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                                    Atual
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center text-sm font-bold text-gray-900">
                                {month.quantidade}
                              </td>
                              <td className="py-3 px-4 text-center text-sm">
                                {index === 0 ? (
                                  <span className="text-gray-400">--</span>
                                ) : (
                                  <span className={`font-medium ${
                                    month.diferenca > 0 ? 'text-green-600' : 
                                    month.diferenca < 0 ? 'text-red-600' : 
                                    'text-gray-600'
                                  }`}>
                                    {month.diferenca > 0 ? '+' : ''}{month.diferenca}
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center text-sm">
                                {index === 0 ? (
                                  <span className="text-gray-400">--</span>
                                ) : (
                                  <div className="flex items-center justify-center gap-1">
                                    <span className={`font-medium ${
                                      month.tipo === 'aumento' ? 'text-green-600' : 
                                      month.tipo === 'reducao' ? 'text-red-600' : 
                                      'text-gray-600'
                                    }`}>
                                      {month.tipo === 'aumento' ? '+' : month.tipo === 'reducao' ? '-' : ''}
                                      {month.variacao.toFixed(1)}%
                                    </span>
                                    <span>
                                      {month.tipo === 'aumento' ? '↗️' : month.tipo === 'reducao' ? '↘️' : '➡️'}
                                    </span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Insights */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">💡 Insights</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>
                          • <strong>Tendência:</strong> {
                            metrics.comparacaoMensal.tipo === 'aumento' 
                              ? `Crescimento de ${metrics.comparacaoMensal.variacaoPercentual.toFixed(1)}% em relação ao mês anterior`
                              : `Redução de ${metrics.comparacaoMensal.variacaoPercentual.toFixed(1)}% em relação ao mês anterior`
                          }
                        </li>
                        <li>
                          • <strong>Média mensal:</strong> {
                            (metrics.comparacaoMensal.historicoMensal.reduce((sum, m) => sum + m.quantidade, 0) / 
                             metrics.comparacaoMensal.historicoMensal.length).toFixed(1)
                          } novos clientes
                        </li>
                        <li>
                          • <strong>Melhor mês:</strong> {
                            (() => {
                              const best = metrics.comparacaoMensal.historicoMensal.reduce((max, m) => 
                                m.quantidade > max.quantidade ? m : max
                              )
                              return `${best.mesCompleto} (${best.quantidade} clientes)`
                            })()
                          }
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}

            {/* Clientes por Região */}
            <ClientesPorRegiaoCard />

            {/* Clientes Recentes */}
            <div className="bg-white rounded-2xl shadow-lg border border-orange-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold gradient-text-orange">Clientes Recentes</h2>
                <p className="text-gray-600 text-sm">Últimos 10 clientes cadastrados</p>
              </div>
              <div className="p-6">
                {recentClients.length > 0 ? (
                  <div className="space-y-4">
                    {recentClients.map((client) => (
                      <div key={client.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-orange-50 transition-colors duration-200">
                        <div className="flex items-center space-x-4">
                          <div className={`w-3 h-3 rounded-full ${
                            client.status === 'ativo' ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          <div>
                            <p className="font-semibold text-gray-900">{client.nome_completo}</p>
                            <p className="text-sm text-gray-600">{client.email || client.celular}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">{formatDate(client.data_cadastro)}</p>
                          <p className={`text-xs font-medium ${
                            client.status === 'ativo' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {client.status === 'ativo' ? 'Ativo' : 'Inativo'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-gray-500">Nenhum cliente cadastrado ainda</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  )
}

export default DashboardPage
